import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import { toast, Bounce } from "react-toastify";
import {
  Building2,
  Calendar,
  FileKey2,
  Hash,
  ArrowRight,
  X,
  UploadCloud,
} from "lucide-react";

export default function FormClient() {
  const [files, setFiles] = useState(null);
  const [cuit, setCuit] = useState("");
  const [alias, setAlias] = useState("");
  const [fechaVencimiento, setFechaVencimiento] = useState(
    new Date().toISOString().split("T")[0]
  );

  const navigate = useNavigate();
  const toMain = () => navigate("/");

  const fileNames = useMemo(() => {
    if (!files || files.length === 0) return [];
    return Array.from(files).map((f) => f.name);
  }, [files]);

  const handleFileChange = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      setFiles(selectedFiles);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!files || files.length === 0 || !cuit || !alias || !fechaVencimiento) {
      toast.info("¡Todos los campos son requeridos!", {
        position: "top-center",
        autoClose: 3000,
        theme: "colored",
        transition: Bounce,
      });
      return;
    }

    const formData = new FormData();
    formData.append("cuit", cuit);
    formData.append("alias", alias);
    formData.append("fechaVencimiento", fechaVencimiento);

    for (let i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }

    try {
      const clienteId = sessionStorage.getItem("_id");

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_PATH}api/clientes/${clienteId}/cuits`,
        { method: "POST", body: formData }
      );

      if (!response.ok) throw new Error("Error al subir el CUIT");

      await response.json();

      toast.success("✅ ¡CUIT agregado correctamente!", {
        position: "top-right",
        autoClose: 2500,
        theme: "colored",
        transition: Bounce,
      });

      // opcional: volver a listado
      navigate("/home");
    } catch (error) {
      toast.error("Hubo un problema al subir el CUIT.", {
        position: "top-right",
        autoClose: false,
        theme: "colored",
        transition: Bounce,
      });
    }
  };

  return (
    <>
      <Header />

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-blue-950 to-slate-950 px-6 py-10">
        {/* Glow */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-blue-500/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-red-500/10 blur-3xl" />
        </div>

        <div className="relative mx-auto w-full max-w-xl">
          {/* Header */}
          <div className="mb-6">
            <p className="text-sm font-semibold text-white/60">FEAPI • Clientes</p>
            <h1 className="mt-2 text-2xl font-bold tracking-tight text-white">
              Nuevo cliente
            </h1>
            <p className="mt-1 text-sm text-white/70">
              Cargá los datos y adjuntá el certificado y clave para operar.
            </p>
          </div>

          {/* Card */}
          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 shadow-xl backdrop-blur">
            <form
              onSubmit={handleSubmit}
              encType="multipart/form-data"
              className="space-y-5"
            >
              {/* CUIT */}
              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">
                  CUIT
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 focus-within:border-white/25">
                  <Hash className="h-4 w-4 text-white/60" />
                  <input
                    value={cuit}
                    onChange={(e) => setCuit(e.target.value)}
                    type="text"
                    placeholder="Ej: 20123456789"
                    autoFocus
                    maxLength={11}
                    onInput={(e) => (e.target.value = e.target.value.replace(/\D/g, ""))}
                    className="w-full bg-transparent text-white placeholder:text-white/40 outline-none"
                  />
                </div>
                <p className="mt-2 text-xs text-white/50">
                  Solo números (11 dígitos).
                </p>
              </div>

              {/* Alias */}
              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">
                  Alias
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 focus-within:border-white/25">
                  <Building2 className="h-4 w-4 text-white/60" />
                  <input
                    value={alias}
                    onChange={(e) => setAlias(e.target.value)}
                    type="text"
                    placeholder="Ej: Empresa XYZ"
                    maxLength={30}
                    className="w-full bg-transparent text-white placeholder:text-white/40 outline-none"
                  />
                </div>
              </div>

              {/* Files */}
              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">
                  Certificado y clave
                </label>

                <label
                  className="group flex cursor-pointer items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 hover:bg-white/10"
                  tabIndex="0"
                  onKeyDown={(e) =>
                    e.key === "Enter" && document.getElementById("fileInput")?.click()
                  }
                >
                  <div className="flex items-center gap-3">
                    <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10">
                      <FileKey2 className="h-5 w-5 text-white/80" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        Adjuntar archivos
                      </p>
                      <p className="text-xs text-white/60">
                        Deben llamarse: <span className="font-semibold">cert.crt</span> y{" "}
                        <span className="font-semibold">key.key</span>
                      </p>
                    </div>
                  </div>

                  <div className="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-semibold text-slate-900 group-hover:bg-white/90">
                    <UploadCloud className="h-4 w-4" />
                    Seleccionar
                  </div>

                  <input
                    id="fileInput"
                    multiple
                    onChange={handleFileChange}
                    type="file"
                    className="hidden"
                  />
                </label>

                {/* Selected files */}
                {fileNames.length > 0 && (
                  <div className="mt-3 rounded-2xl border border-white/10 bg-white/5 p-4">
                    <p className="text-xs font-semibold text-white/70">
                      Archivos seleccionados
                    </p>
                    <ul className="mt-2 space-y-1">
                      {fileNames.map((n) => (
                        <li key={n} className="text-xs text-white/70">
                          • {n}
                        </li>
                      ))}
                    </ul>
                    <button
                      type="button"
                      onClick={() => setFiles(null)}
                      className="mt-3 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-xs font-semibold text-white hover:bg-white/10"
                    >
                      <X className="h-4 w-4" />
                      Quitar archivos
                    </button>
                  </div>
                )}
              </div>

              {/* Fecha */}
              <div>
                <label className="mb-1 block text-sm font-medium text-white/80">
                  Fecha de expiración
                </label>
                <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 focus-within:border-white/25">
                  <Calendar className="h-4 w-4 text-white/60" />
                  <input
                    value={fechaVencimiento}
                    onChange={(e) => setFechaVencimiento(e.target.value)}
                    type="date"
                    className="w-full bg-transparent text-white placeholder:text-white/40 outline-none [color-scheme:dark]"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
                <button
                  type="button"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
                  onClick={toMain}
                >
                  <X className="h-4 w-4" />
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Guardar <ArrowRight className="h-4 w-4" />
                </button>
              </div>

              <p className="pt-2 text-center text-xs text-white/50">
                Los datos son de producción. Cargá información real y verificá los nombres de los archivos.
              </p>
            </form>
          </div>

          <footer className="mt-6 text-center text-xs text-white/45">
            © {new Date().getFullYear()} FEAPI
          </footer>
        </div>
      </div>
    </>
  );
}
